import { CacheContext } from 'components/cacheContextObject';
import React, { useContext, useMemo } from 'react';
import EventCache from 'utils/EventCache/EventCache';
import { type PlaylistCache, PlaylistCacheImpl } from 'utils/PlaylistCache/PlaylistCache';
import SegmentCache from 'utils/PlaylistCache/SegmentCache';

export const CacheProvider: React.FC<{
  playlistCache?: PlaylistCache;
  segmentCache?: SegmentCache;
  eventCache?: EventCache;
  children: React.ReactNode;
}> = (props) => {
  // Create each cache once. A new instance on every render drops playlist data when a parent re-renders.
  const caches = useMemo(() => {
    const playlistCache = props.playlistCache || new PlaylistCacheImpl();
    const segmentCache = props.segmentCache || new SegmentCache(playlistCache);
    const eventCache = props.eventCache || new EventCache();
    return { playlistCache, segmentCache, eventCache };
  }, [props.playlistCache, props.segmentCache, props.eventCache]);

  return <CacheContext.Provider value={caches}>{props.children}</CacheContext.Provider>;
};

function useCaches() {
  const caches = useContext(CacheContext);
  if (!caches) {
    throw new Error(`Missing CacheProvider`);
  }
  return caches;
}

export function usePlaylistCache(): PlaylistCache {
  return useCaches().playlistCache;
}

export function useSegmentCache(): SegmentCache {
  return useCaches().segmentCache;
}

export function useEventCache(): EventCache {
  return useCaches().eventCache;
}
